import React from "react";
import { Box, Typography, styled } from "@mui/material";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import { useGetDeliveryZonesQuery } from "../../services/deliveryZones/deliveryZones";
import {
  findActiveZone,
  useDeliverySelection,
  useReconcileMunicipality,
} from "../../hooks/useDeliverySelection";
import { AvailabilityInput, resolveLeadTimeDays } from "../../utils/availability";
import {
  composePromise,
  DELIVERY_HOME,
  isQuoteError,
  quoteDelivery,
} from "../../utils/deliveryQuote";
import { calculateDiscountedPrice, formatCOP } from "../../utils/format";
import { DEFAULT_LEAD_TIME_DAYS, DELIVERY_TEXTS } from "../../pages/shared/constants";
import MunicipalitySelect from "./MunicipalitySelect";

const Strip = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.75),
  padding: theme.spacing(1.5),
  marginTop: theme.spacing(1),
  borderRadius: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
}));

const Row = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  flexWrap: "wrap",
}));

const Fee = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  color: theme.palette.success.main,
}));

type Props = {
  /** The product, for its own lead time — the zone's days are added to it, never substituted. */
  item: AvailabilityInput & { price?: number | string | null; discount?: number };
};

/**
 * "Enviar a Medellín · Envío $15.000 · Llega en 7–8 días hábiles", under the buy block.
 *
 * This is the anti-abandonment piece (§5.5). Baymard puts unexpected extra costs at 39 % of
 * checkout abandonments, and a shipping fee is only unexpected if the first time you see it is the
 * last screen before paying. It is also the cheapest possible version of that fix: the zone list is
 * one query the checkout already needs.
 *
 * It renders nothing at all when there are no zones — a stage that has never been seeded shows the
 * product page it showed before this existed, rather than an empty control implying a broken shop.
 */
const DeliveryEstimate: React.FC<Props> = ({ item }) => {
  const { data } = useGetDeliveryZonesQuery();
  const { municipalityId, setMunicipalityId } = useDeliverySelection();
  useReconcileMunicipality(data?.zones, municipalityId, setMunicipalityId);

  if (!data || data.zones.length === 0) return null;

  const zone = findActiveZone(data.zones, municipalityId);
  const defaultLeadTimeDays = data.config?.default_lead_time_days ?? DEFAULT_LEAD_TIME_DAYS;

  // One unit of this product, which is what the page is showing. The cart's real subtotal decides
  // free shipping at checkout; here it only ever under-promises, never over-promises.
  const unitPrice = calculateDiscountedPrice(Number(item.price) || 0, item.discount ?? 0);
  const quote = quoteDelivery({ method: DELIVERY_HOME, zone, itemsSubtotal: unitPrice });
  const promise = isQuoteError(quote)
    ? null
    : composePromise({
        cartLeadTimeDays: resolveLeadTimeDays(item, defaultLeadTimeDays),
        etaDaysMin: quote.etaDaysMin,
        etaDaysMax: quote.etaDaysMax,
      });

  return (
    <Strip>
      <Row>
        <LocalShippingIcon fontSize="small" color="action" />
        <Typography variant="body2" fontWeight={700}>
          {DELIVERY_TEXTS.shipTo}
        </Typography>
        <MunicipalitySelect
          zones={data.zones}
          value={municipalityId}
          onChange={setMunicipalityId}
        />
      </Row>

      {!isQuoteError(quote) && (
        <Row>
          <Fee variant="body2">
            {quote.free || quote.fee === 0
              ? DELIVERY_TEXTS.freeShipping
              : DELIVERY_TEXTS.feeValue(formatCOP(quote.fee))}
          </Fee>
          <Typography variant="body2" color="text.secondary">
            {/* A quote line has no knowable date, so it says so rather than promising the zone's
                own days for goods nobody has priced yet. */}
            {promise
              ? DELIVERY_TEXTS.promise(promise.daysMin, promise.daysMax)
              : DELIVERY_TEXTS.promiseUnknown}
          </Typography>
        </Row>
      )}
    </Strip>
  );
};

export default DeliveryEstimate;
